import { round } from "../../../utils/math";
import { normalizeRole, RoleS } from "../../../utils/role-helpers";
import { QueueTypeS } from "../../../utils/queue-type-helpers";
import { ObjectivesTierList, Champion, Role, RoleData } from "../../../interfaces/tier-lists/objectives-tier-list.interface";

export interface ObjectivesChampionInfo {
  role: RoleS;
  champion_id: Champion[0];
  champion_link: {
    champion_id: Champion[0];
    role: RoleS;
  };
  wins: Champion[1];
  matches: Champion[2];
  win_rate: number;
  pick_rate: number;
  gold: number;
  cs: number;
  jungle_cs: number;
  enemy_jungle_cs: number;
  turrets: number;
  damage_to_turrets: number;
  wards_killed: number;
  match_duration: number;
}

export function cleanObjectives(data: ObjectivesTierList) {
  const objectives: Partial<Record<RoleS, ObjectivesChampionInfo[]>> = {};
  let objectivesData = data[0];
  const last_updated = data[1];
  const total_matches = data[2];

  const entries: Array<[Role, RoleData]> = Object.entries(objectivesData) as any;
  for (const [role, roleData] of entries) {
    const normalizedRole = normalizeRole(role) as RoleS;
    const role_array: ObjectivesChampionInfo[] = [];
    roleData.forEach((champInfo) => {
      const matches = champInfo[2];

      const cur_champ_info = {
        role: normalizedRole,
        champion_id: champInfo[0],
        champion_link: {
          champion_id: champInfo[0],
          role: normalizedRole,
        },
        wins: champInfo[1],
        matches,
        win_rate: round((champInfo[1] / matches) * 100, 2),
        pick_rate: round((matches / total_matches) * 100, 2),
        gold: round(champInfo[3] / matches, 0),
        cs: round(champInfo[4] / matches, 1),
        jungle_cs: round(champInfo[5] / matches, 1),
        enemy_jungle_cs: round(champInfo[6] / matches, 1),
        turrets: round(champInfo[7] / matches, 1),
        damage_to_turrets: round(champInfo[8] / matches, 0),
        wards_killed: round(champInfo[9] / matches, 1),
        match_duration: round(champInfo[10] / matches / 60, 2),
      };

      role_array.push(cur_champ_info);
    });

    const sorted_role_array = role_array.sort((a, b) => {
      // Sort by Win Rate
      return b.win_rate - a.win_rate;
    });

    objectives[normalizedRole] = sorted_role_array;
  }

  const roleArray = Object.values(objectives);
  const all_roles_array: ObjectivesChampionInfo[] = roleArray.reduce((acc, curr) => {
    return [...acc, ...curr];
  }, []);
  const sorted_roles_array = all_roles_array.sort((a, b) => {
    // Sort by Win Rate
    return b.win_rate - a.win_rate;
  });

  objectives[RoleS.ALL] = sorted_roles_array;

  return {
    objectives,
    last_updated,
    total_matches,
  };
}

export function selectObjectivesData(
  objectivesData: ReturnType<typeof cleanObjectives>,
  params: { queueType: string; role: string },
) {
  let objectives, last_updated, total_matches;

  if (!objectivesData) {
    return { objectives, last_updated, total_matches };
  }

  let { queueType, role } = params;

  if (queueType === QueueTypeS.ARAM || queueType == QueueTypeS.NEXUS_BLITZ) {
    role = RoleS.NONE;
  } else {
    role = normalizeRole(role, RoleS.ALL);
  }

  if (objectivesData) {
    objectives = objectivesData.objectives[role as RoleS];
    last_updated = objectivesData.last_updated;
    total_matches = objectivesData.total_matches;
  }

  return { objectives, last_updated, total_matches };
}
